// Asset preview / art QC scene (DESIGN §9 step 8). Reachable from the menu and
// with ?scene=AssetPreview. Tabs: MECHS (every animation, both directions, full
// loadouts, overlays, export), ENEMIES, BOSSES, PARTS (painted source parts), FX,
// ENVIRONMENT, ICONS. Mouse + keyboard: 1–7 switch tabs, Esc returns to the menu; each tab lists its
// own shortcuts. Every texture is guarded so the scene renders even while other
// modules are still stubs.

import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH, SCENES } from '../config/constants';
import { COLORS } from '../config/palette';
import { BossTab, EnemyTab, IconTab } from '../debug/preview/enemyTab';
import { EnvTab } from '../debug/preview/envTab';
import { FxTab } from '../debug/preview/fxTab';
import { MechTab } from '../debug/preview/mechTab';
import { PartsTab } from '../debug/preview/partsTab';
import type { PreviewTab } from '../debug/preview/tab';
import { Button, PV, label } from '../debug/preview/widgets';

type TabId = 'mechs' | 'enemies' | 'bosses' | 'parts' | 'fx' | 'environment' | 'icons';

const TABS: { id: TabId; name: string; make: (s: Phaser.Scene) => PreviewTab }[] = [
  { id: 'mechs', name: 'MECHS', make: (s) => new MechTab(s) },
  { id: 'enemies', name: 'ENEMIES', make: (s) => new EnemyTab(s) },
  { id: 'bosses', name: 'BOSSES', make: (s) => new BossTab(s) },
  { id: 'parts', name: 'PARTS', make: (s) => new PartsTab(s) },
  { id: 'fx', name: 'FX', make: (s) => new FxTab(s) },
  { id: 'environment', name: 'ENVIRONMENT', make: (s) => new EnvTab(s) },
  { id: 'icons', name: 'ICONS', make: (s) => new IconTab(s) },
];

export interface AssetPreviewSceneData {
  tab?: TabId;
}

export class AssetPreviewScene extends Phaser.Scene {
  private tab: PreviewTab | null = null;
  private tabId: TabId = 'mechs';
  private tabButtons = new Map<TabId, Button>();
  private errorText!: Phaser.GameObjects.Text;

  constructor() {
    super(SCENES.PREVIEW);
  }

  create(data: AssetPreviewSceneData = {}): void {
    this.tab = null;
    this.tabButtons.clear();
    this.cameras.main.setBackgroundColor(COLORS.uiBg);
    this.input.setDefaultCursor('default');
    this.drawBackdrop();
    this.drawHeader();

    // ?tab=fx (URL) or scene data selects the initial tab.
    let initial: TabId = data?.tab ?? 'mechs';
    try {
      const q = new URLSearchParams(window.location.search).get('tab') as TabId | null;
      if (q && TABS.some((t) => t.id === q)) initial = q;
    } catch {
      // No window (never in the browser build).
    }
    this.selectTab(initial);

    this.input.keyboard?.on('keydown', (ev: KeyboardEvent) => this.onKey(ev));
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.keyboard?.removeAllListeners('keydown');
      this.tab?.destroy();
      this.tab = null;
    });
  }

  update(_time: number, deltaMs: number): void {
    if (!this.tab) return;
    try {
      this.tab.update(Math.min(deltaMs / 1000, 0.1));
    } catch (err) {
      // A broken asset must not take the whole preview down; show it and keep the scene alive.
      this.errorText.setText(`TAB ERROR: ${String((err as Error)?.message ?? err)}`);
      console.error('[preview] tab update failed', err);
    }
  }

  private drawBackdrop(): void {
    const g = this.add.graphics();
    g.fillStyle(0x070a0f, 1).fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    // Faint 48 px grid = world tile size, so sizes read against gameplay scale.
    g.lineStyle(1, COLORS.steel0, 1);
    for (let x = 0; x <= GAME_WIDTH; x += 48) g.lineBetween(x, PV.headerH, x, GAME_HEIGHT);
    for (let y = PV.headerH; y <= GAME_HEIGHT; y += 48) g.lineBetween(0, y, GAME_WIDTH, y);
  }

  private drawHeader(): void {
    const g = this.add.graphics();
    g.fillStyle(COLORS.uiPanel, 1).fillRect(0, 0, GAME_WIDTH, PV.headerH);
    g.lineStyle(1, COLORS.uiBorder, 1).lineBetween(0, PV.headerH, GAME_WIDTH, PV.headerH);
    g.fillStyle(COLORS.volt, 1).fillRect(PV.pad, 18, 4, 28);
    label(this, PV.pad + 16, 12, 'NEON CORE', { font: 'display', size: 13, color: COLORS.uiDim, spacing: 4 });
    label(this, PV.pad + 16, 26, 'ASSET PREVIEW', { font: 'display', size: 24, weight: 700, spacing: 4 });

    const x0 = 360;
    TABS.forEach((t, i) => {
      const b = new Button(this, x0 + i * 176, 13, t.name, () => this.selectTab(t.id), { w: 168, h: 38, toggle: true, hint: String(i + 1), size: 15 });
      this.tabButtons.set(t.id, b);
    });
    new Button(this, GAME_WIDTH - PV.pad - 200, 13, 'BACK TO MENU', () => this.back(), { w: 200, h: 38, accent: COLORS.uiBad, hint: 'ESC', size: 14, align: 'left' });
    this.errorText = label(this, GAME_WIDTH - PV.pad - 216, 34, '', { font: 'mono', size: 11, color: COLORS.uiBad, originX: 1 });
  }

  private selectTab(id: TabId): void {
    const def = TABS.find((t) => t.id === id) ?? TABS[0];
    this.tabId = def.id;
    for (const [tid, b] of this.tabButtons) b.setActive(tid === def.id);
    if (this.tab) {
      try {
        this.tab.destroy();
      } catch (err) {
        console.error('[preview] tab destroy failed', err);
      }
      this.tab = null;
    }
    this.errorText?.setText('');
    try {
      this.tab = def.make(this);
    } catch (err) {
      console.error(`[preview] tab "${def.id}" failed to build`, err);
      this.errorText.setText(`${def.name} FAILED: ${String((err as Error)?.message ?? err)}`);
    }
  }

  private onKey(ev: KeyboardEvent): void {
    if (ev.code === 'Escape') {
      this.back();
      return;
    }
    const digit = /^Digit([1-9])$/.exec(ev.code);
    if (digit) {
      const t = TABS[Number(digit[1]) - 1];
      if (t) this.selectTab(t.id);
      return;
    }
    if (ev.code === 'Tab') {
      ev.preventDefault();
      const i = TABS.findIndex((t) => t.id === this.tabId);
      this.selectTab(TABS[(i + (ev.shiftKey ? TABS.length - 1 : 1)) % TABS.length].id);
      return;
    }
    try {
      this.tab?.key?.(ev.code);
    } catch (err) {
      console.error('[preview] key handler failed', err);
    }
  }

  private back(): void {
    this.scene.start(SCENES.MENU);
  }
}
